import { useEffect, useRef } from 'react';

import { makeNamespacedLog } from '@sb/log';
import type { RoutineRunnerState } from '@sb/routine-runner';
import { useDevTokens } from '@sbrc/services';
import { isTabletApp } from '@sbrc/utils/userAgent';

import useRobotState from './useRobotState';

const log = makeNamespacedLog('useRoutineTeleopModalBridge');

const IPAD_TELEOP_MODE = 'iosTablet';
const TOKEN_SOURCE = 'ipad_teleop';

interface TeleopBridgeState {
  kind: 'RoutineRunning';
  currentStepID: string;
  currentStepKind: string | null;
  /** Mode arg from the current Teleop step, if applicable. */
  stepMode: string | null;
}

function selectTeleopBridgeState(
  state: RoutineRunnerState | null,
): TeleopBridgeState | null {
  if (state?.kind !== 'RoutineRunning') return null;

  const stepMode =
    state.currentStepKind === 'Teleop' && state.currentStep != null
      ? // currentStep.args is typed as `Args = null as any` on the base class;
        // narrow to the Teleop args shape we care about.
        (state.currentStep.args as { mode?: string } | null)?.mode ?? null
      : null;

  return {
    kind: 'RoutineRunning',
    currentStepID: state.currentStepID,
    currentStepKind: state.currentStepKind,
    stepMode,
  };
}

/**
 * Summons the iPad teleop FloatingPanel when a running routine enters a Teleop
 * step configured for iOS-tablet mode. Reuses the existing `teleopModalShow`
 * custom event that tablet-app's WebViewModel listens for.
 *
 * No-op on non-tablet-app, on non-iosTablet steps, and for any step kind other
 * than Teleop. Guards against double-dispatch by keying on currentStepID.
 */
export function useRoutineTeleopModalBridge(): void {
  const bridgeState = useRobotState({}, selectTeleopBridgeState);
  const { createEphemeralToken, deleteTokensBySource } = useDevTokens();
  const dispatchedForStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    const shouldFire =
      isTabletApp() &&
      bridgeState?.kind === 'RoutineRunning' &&
      bridgeState.currentStepKind === 'Teleop' &&
      bridgeState.stepMode === IPAD_TELEOP_MODE &&
      bridgeState.currentStepID != null &&
      bridgeState.currentStepID !== '';

    if (!shouldFire) {
      // Reset so the next matching step will fire even if kind/mode stay the same.
      if (
        bridgeState?.kind !== 'RoutineRunning' ||
        bridgeState.currentStepKind !== 'Teleop' ||
        bridgeState.stepMode !== IPAD_TELEOP_MODE
      ) {
        dispatchedForStepIdRef.current = null;
      }

      return undefined;
    }

    const stepId = bridgeState.currentStepID;

    if (dispatchedForStepIdRef.current === stepId) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        await deleteTokensBySource(TOKEN_SOURCE);

        const token = await createEphemeralToken(
          'iPad Teleop (routine)',
          TOKEN_SOURCE,
        );

        if (cancelled || !token) return;

        // Dedup inside the async so cancel-during-await doesn't permanently
        // suppress this step: the ref is only committed once we're about to
        // dispatch, at which point a concurrent re-run will observe it and bail.
        if (dispatchedForStepIdRef.current === stepId) return;
        dispatchedForStepIdRef.current = stepId;

        document.dispatchEvent(
          new CustomEvent('teleopModalShow', {
            detail: { publicApiToken: token },
          }),
        );
      } catch (err) {
        // Token service can fail (network, auth service down). Swallow the
        // rejection so it doesn't surface as an unhandled promise rejection;
        // dispatchedForStepIdRef stays null so the next render-driven attempt
        // (e.g. when the user retries by re-entering the step) can proceed.
        if (!cancelled) {
          log.error('Failed to prepare iPad teleop token', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    bridgeState?.kind,
    bridgeState?.currentStepID,
    bridgeState?.currentStepKind,
    bridgeState?.stepMode,
    createEphemeralToken,
    deleteTokensBySource,
  ]);
}
